import type { RibbonControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/Controls/RibbonControlProps';
import { computedFn } from 'mobx-utils/lib/computedFn';
import { getDefaultRibbonStyles } from 'owa-command-ribbon-styles/lib/util/getDefaultRibbonStyles';
import { getComposeViewState } from 'owa-mail-reading-pane-store/lib/utils/getComposeViewState';
import type { ComposeViewState } from 'owa-mail-compose-store';
import type { RibbonControlDefinitionProps } from 'owa-mail-ribbon-utils';
import type { RibbonControlId } from 'owa-ribbon-ids';
import {
    ActionControlId,
    MailRibbonControlId,
    MailRibbonTabId,
    FormatControlId,
} from 'owa-ribbon-ids';
import * as SharedControlDef from '../../../sharedControls/composeSharedControlDefinitions';
import * as ControlDef from './slrM3MessageTabControlDefinitions';
import { getAlignCenterControl } from 'owa-compose-formatcontrols/lib/components/alignCenter';
import { getAlignLeftControl } from 'owa-compose-formatcontrols/lib/components/alignLeft';
import { getAlignRightControl } from 'owa-compose-formatcontrols/lib/components/alignRight';
import { getBoldControl } from 'owa-compose-formatcontrols/lib/components/bold';
import { getBulletsControl } from 'owa-compose-formatcontrols/lib/components/bullets';
import { getChangeCaseControl } from 'owa-compose-formatcontrols/lib/components/changeCase';
import { getFontBackColorPickerControl } from 'owa-compose-formatcontrols/lib/components/fontBackColorPicker';
import { getFontColorPickerControl } from 'owa-compose-formatcontrols/lib/components/fontColorPicker';
import { getFontNamePickerControl } from 'owa-compose-formatcontrols/lib/components/fontNamePicker';
import { getFontSizePickerControl } from 'owa-compose-formatcontrols/lib/components/fontSizePicker';
import { getFormatPainterControl } from 'owa-compose-formatcontrols/lib/components/formatPainter';
import { getIndentControl } from 'owa-compose-formatcontrols/lib/components/indent';
import { getItalicControl } from 'owa-compose-formatcontrols/lib/components/italic';
import { getLeftToRightControl } from 'owa-compose-formatcontrols/lib/components/ltr';
import { getNumberingControl } from 'owa-compose-formatcontrols/lib/components/numbering';
import { getOutdentControl } from 'owa-compose-formatcontrols/lib/components/outdent';
import { getPasteCutCopyControl } from 'owa-compose-formatcontrols/lib/components/pasteCutCopy';
import { getQuoteControl } from 'owa-compose-formatcontrols/lib/components/quote';
import { getRemoveFormatControl } from 'owa-compose-formatcontrols/lib/components/removeFormat';
import { getRightToLeftControl } from 'owa-compose-formatcontrols/lib/components/rtl';
import { getStrikeThroughControl } from 'owa-compose-formatcontrols/lib/components/strikethrough';
import { getSubscriptControl } from 'owa-compose-formatcontrols/lib/components/subscript';
import { getSuperscriptControl } from 'owa-compose-formatcontrols/lib/components/superscript';
import { getUnderlineControl } from 'owa-compose-formatcontrols/lib/components/underline';
import { getUndoRedoControl } from 'owa-compose-formatcontrols/lib/components/undoRedo';
import { getAddIns } from 'owa-compose-actioncontrols/lib/components/controls/addins';
import { getControlInfo } from 'owa-mail-ribbon-utils';
import { getAddInlineImagesControl } from 'owa-compose-actioncontrols/lib/components/controls/inlineImages';
import { getAddEmoji } from 'owa-compose-actioncontrols/lib/components/controls/emoji';
import { getAccChecker } from 'owa-compose-actioncontrols/lib/components/controls/accChecker';
import { getFindTime } from 'owa-compose-actioncontrols/lib/components/controls/findTime';
import { getAccCheckerState } from 'owa-mail-compose-ribbon/lib/utils/getAccCheckerState';
import { callEditorApi } from 'owa-editor/lib/utils/callEditorApi';
import { lazyInsertContentForFindTime } from 'findtime-compose';
import { getProofingOptions } from 'owa-compose-actioncontrols/lib/components/controls/proofingOptions';
import { getShowMessageOptions } from 'owa-compose-actioncontrols/lib/components/controls/showMessageOptions';
import { showMessageOptionsDialog, switchBodyType } from 'owa-mail-compose-command';
import { getComposeRibbonId } from 'owa-compose-ribbon-utils/lib/utils/getComposeRibbonId';
import { onAddInlineImage } from 'owa-mail-compose-ribbon';
import { constructKeytip, getKeytipString } from 'owa-command-ribbon';
import type EditorViewState from 'owa-editor/lib/store/schema/EditorViewState';
import { noSubject } from 'owa-locstrings/lib/strings/nosubject.locstring.json';
import loc from 'owa-localize';
import { getSwitchBodyType } from 'owa-compose-actioncontrols/lib/components/controls/switchBodyType';
import EmailAddressWrapper from 'owa-service/lib/contract/EmailAddressWrapper';
import { proofingStore } from 'owa-editor-proofing-plugin/lib/store/store';
import setImportance from 'owa-mail-compose-actions/lib/actions/setImportance';
import { getImportance } from 'owa-compose-actioncontrols/lib/components/controls/setImportance';
import type ImportanceType from 'owa-service/lib/contract/ImportanceType';
import { getTableControl } from 'owa-compose-actioncontrols/lib/components/controls/insertTable/getTableControl';

/**
 * This function supplies a mapping from RibbonControlId to their respective control definition function.
 * For any new control in a tab, be sure to update the mapping below.
 * Note: This function should *not* have any logic regarding showing/hiding.
 *       That should be in shouldShowRibbonControl.ts instead.
 */
export const getSLRM3MessageTabControlDefinition = computedFn(
    (
        controlId: RibbonControlId,
        props: RibbonControlDefinitionProps
    ): RibbonControlProps | undefined => {
        const viewState: ComposeViewState | undefined =
            props.popoutComposeViewState ?? getComposeViewState();
        if (!viewState) {
            return undefined;
        }
        const defaultStyles = getDefaultRibbonStyles();
        const editorId = viewState.editorId;
        const formatViewState = viewState.ribbon.formatViewState;

        switch (controlId) {
            case ActionControlId.Sensitivity:
                return ControlDef.getSensitivityControl(props);
            case MailRibbonControlId.AttachFile:
                return SharedControlDef.getAttachFileControl(
                    props.attachmentPickers!.addFilesAsAttachments!, // Cannot be undefined, because shouldShowRibbonControl checks for this
                    viewState,
                    MailRibbonTabId.Tab_M3Message
                );
            case MailRibbonControlId.AttachSig:
                return SharedControlDef.getAttachSigControl(
                    viewState,
                    MailRibbonTabId.Tab_M3Message
                );
            case MailRibbonControlId.FormatPainter:
                return getFormatPainterControl(
                    editorId,
                    formatViewState,
                    defaultStyles,
                    MailRibbonTabId.Tab_M3Message
                );

            case MailRibbonControlId.Paste:
                return getPasteCutCopyControl(
                    viewState,
                    defaultStyles,
                    MailRibbonTabId.Tab_M3Message
                );
            case MailRibbonControlId.Save:
                return ControlDef.getSaveControl(viewState);

            case FormatControlId.Bold:
                return getBoldControl(
                    editorId,
                    formatViewState,
                    defaultStyles,
                    MailRibbonTabId.Tab_M3Message
                );
            case FormatControlId.Italic:
                return getItalicControl(
                    editorId,
                    formatViewState,
                    defaultStyles,
                    MailRibbonTabId.Tab_M3Message
                );
            case FormatControlId.Underline:
                return getUnderlineControl(
                    editorId,
                    formatViewState,
                    defaultStyles,
                    MailRibbonTabId.Tab_M3Message
                );
            case FormatControlId.StrikeThrough:
                return getStrikeThroughControl(
                    editorId,
                    formatViewState,
                    defaultStyles,
                    MailRibbonTabId.Tab_M3Message
                );
            case FormatControlId.Bullets:
                return getBulletsControl(
                    editorId,
                    formatViewState,
                    defaultStyles,
                    MailRibbonTabId.Tab_M3Message
                );
            case FormatControlId.Numbering:
                return getNumberingControl(
                    editorId,
                    formatViewState,
                    defaultStyles,
                    MailRibbonTabId.Tab_M3Message
                );
            case FormatControlId.AlignLeft:
                return getAlignLeftControl(editorId, defaultStyles, MailRibbonTabId.Tab_M3Message);
            case FormatControlId.AlignCenter:
                return getAlignCenterControl(
                    editorId,
                    defaultStyles,
                    MailRibbonTabId.Tab_M3Message
                );
            case FormatControlId.AlignRight:
                return getAlignRightControl(editorId, defaultStyles, MailRibbonTabId.Tab_M3Message);
            case FormatControlId.LeftToRight:
                return getLeftToRightControl(
                    editorId,
                    defaultStyles,
                    MailRibbonTabId.Tab_M3Message
                );
            case FormatControlId.RightToLeft:
                return getRightToLeftControl(
                    editorId,
                    defaultStyles,
                    MailRibbonTabId.Tab_M3Message
                );
            case FormatControlId.UndoRedo:
                return getUndoRedoControl(
                    editorId,
                    formatViewState,
                    defaultStyles,
                    MailRibbonTabId.Tab_M3Message
                );
            case FormatControlId.Quote:
                return getQuoteControl(
                    editorId,
                    formatViewState,
                    defaultStyles,
                    MailRibbonTabId.Tab_M3Message
                );
            case FormatControlId.ChangeCase:
                return getChangeCaseControl(
                    editorId,
                    defaultStyles,
                    defaultStyles,
                    MailRibbonTabId.Tab_M3Message
                );
            case FormatControlId.Subscript:
                return getSubscriptControl(
                    editorId,
                    formatViewState,
                    defaultStyles,
                    MailRibbonTabId.Tab_M3Message
                );
            case FormatControlId.Superscript:
                return getSuperscriptControl(
                    editorId,
                    formatViewState,
                    defaultStyles,
                    MailRibbonTabId.Tab_M3Message
                );
            case FormatControlId.Indent:
                return getIndentControl(editorId, defaultStyles, MailRibbonTabId.Tab_M3Message);
            case FormatControlId.Outdent:
                return getOutdentControl(editorId, defaultStyles, MailRibbonTabId.Tab_M3Message);
            case FormatControlId.FontColorPicker:
                return getFontColorPickerControl(
                    editorId,
                    formatViewState,
                    MailRibbonTabId.Tab_M3Message
                );
            case FormatControlId.FontBackColorPicker:
                return getFontBackColorPickerControl(
                    editorId,
                    formatViewState,
                    MailRibbonTabId.Tab_M3Message
                );
            case FormatControlId.FontNamePicker:
                return getFontNamePickerControl(
                    editorId,
                    formatViewState,
                    MailRibbonTabId.Tab_M3Message
                );
            case FormatControlId.FontSizePicker:
                return getFontSizePickerControl(
                    editorId,
                    formatViewState,
                    MailRibbonTabId.Tab_M3Message
                );
            case FormatControlId.FormatPainter:
                return getFormatPainterControl(
                    editorId,
                    formatViewState,
                    defaultStyles,
                    MailRibbonTabId.Tab_M3Message
                );
            case FormatControlId.RemoveFormat:
                return getRemoveFormatControl(
                    editorId,
                    defaultStyles,
                    MailRibbonTabId.Tab_M3Message
                );
            case FormatControlId.PasteCutCopy:
                return getPasteCutCopyControl(
                    viewState,
                    defaultStyles,
                    MailRibbonTabId.Tab_M3Message
                );
            case MailRibbonControlId.ShowLinkDialog:
                return SharedControlDef.getLinkControl(viewState, props.targetWindow);
            case ActionControlId.GetAddIns:
                return getAddIns(viewState, undefined);
            case MailRibbonControlId.Pictures:
                const controlInfo = getControlInfo(MailRibbonControlId.Pictures);
                return getAddInlineImagesControl(
                    getComposeRibbonId(MailRibbonControlId.Pictures, viewState),
                    onAddInlineImage(props.attachmentPickers!.addImagesInline!.computer.current),
                    undefined /* styles */,
                    controlInfo.text,
                    controlInfo.tooltip,
                    false /* isButtonDisabled */,
                    constructKeytip(
                        [getKeytipString(MailRibbonTabId.Tab_M3Message)],
                        getKeytipString(MailRibbonControlId.Pictures)
                    ),
                    controlInfo.iconName
                );
            case ActionControlId.Emoji:
                return getAddEmoji(viewState, viewState.expressionId!, undefined);
            case ActionControlId.CheckForAccessibilityIssues:
                return getAccChecker(viewState!, getAccCheckerState(viewState!)!, undefined);
            case ActionControlId.SwitchBodyType:
                const onSwitchBodyType = () => {
                    if (viewState) {
                        const targetWindow =
                            callEditorApi(viewState, 'getDocument')?.defaultView || window;
                        switchBodyType(viewState, targetWindow);
                    }
                };
                const isPlainText = viewState.bodyType != 'HTML';
                return getSwitchBodyType(editorId, isPlainText, onSwitchBodyType, undefined);

            case ActionControlId.FindTime:
                const getAllRecipientsFunc = (composeViewState?: ComposeViewState) => () => {
                    return [
                        composeViewState?.toRecipientWell?.recipients?.map(
                            recipient => recipient.persona.EmailAddress
                        ) ?? [],
                        composeViewState?.ccRecipientWell?.recipients?.map(
                            recipient => recipient.persona.EmailAddress
                        ) ?? [],
                        composeViewState?.bccRecipientWell?.recipients?.map(
                            recipient => recipient.persona.EmailAddress
                        ) ?? [],
                    ] as EmailAddressWrapper[][];
                };
                const getSubjectFunc = (composeViewState?: ComposeViewState) => () => {
                    return (composeViewState?.subject || '').trim() || loc(noSubject);
                };
                const insertContentForFindTimeFunc = (editorViewState?: EditorViewState) => (
                    content: string
                ) => {
                    if (editorViewState) {
                        return lazyInsertContentForFindTime.importAndExecute(
                            editorViewState,
                            content
                        );
                    }
                    return null;
                };
                return getFindTime(
                    viewState,
                    'MailComposeView' /* source */,
                    getAllRecipientsFunc(viewState),
                    getSubjectFunc(viewState),
                    insertContentForFindTimeFunc(viewState),
                    undefined
                );
            case ActionControlId.ProofingOptions:
                const proofingDisabled =
                    viewState.proofing?.isProofingDisabled ||
                    !proofingStore.editorBxClients.has(viewState.proofing?.editorProofingId!);
                return getProofingOptions(viewState!, proofingDisabled, undefined);
            case ActionControlId.ShowMessageOptions:
                const onMessageOptionDialog = () => {
                    if (viewState) {
                        const targetWindow =
                            callEditorApi(viewState, 'getDocument')?.defaultView || window;
                        showMessageOptionsDialog(viewState, targetWindow);
                    }
                };
                return getShowMessageOptions(viewState!, onMessageOptionDialog, undefined);
            case ActionControlId.SetImportance:
                const onImportanceToggled = (viewState: ComposeViewState | undefined) => (
                    importance: ImportanceType
                ) =>
                    viewState &&
                    setImportance(
                        viewState,
                        viewState.importance === importance ? 'Normal' : importance
                    );

                return getImportance(
                    editorId,
                    viewState.importance,
                    onImportanceToggled(viewState),
                    undefined
                );
            case ActionControlId.InsertTable:
                return getTableControl(
                    getComposeRibbonId(ActionControlId.InsertTable, viewState),
                    viewState,
                    undefined,
                    undefined,
                    false
                );

            case MailRibbonControlId.BasicTextOverFlow:
                const isPopout = props.popoutComposeViewState != undefined;
                return ControlDef.getBasicTextOverFlow(viewState, isPopout);
            default:
                throw new Error(
                    'Unknown control: ' +
                        controlId +
                        ' searched for in getSLRM3MessageTabControlDefinition'
                );
        }
    }
);
